import type { Tone } from "@/components/ui/badge";
import { LOCATION_LABELS } from "@/lib/calendars";
import { scheduledFor } from "@/lib/texts";
import type {
  Appointment, AppointmentConfirmation, AppointmentEvent, AppointmentStatus, MeetingLocationKind,
} from "@/lib/types";

/**
 * Appointments as the console shows them (ACL-291, ADR-085 in `platform`): their statuses, their
 * times on the calendar's clock, and their history in words. The rules are core-api's
 * (`routers/appointments.py`): a cancelled or rescheduled appointment is closed, and `rescheduled`
 * is set only by rescheduling. Pure, for `test/appointments.test.ts`.
 */

/**
 * Each status's words and its colour (ACL-525, after GoHighLevel's): the one map the badges, the
 * calendar's chips and the status pickers all read, so a status is the same colour everywhere.
 * No two settable statuses share a tone; a colour always goes with the label, never alone.
 */
export const STATUS_INFO: Record<AppointmentStatus, { label: string; tone: Tone }> = {
  scheduled: { label: "Scheduled", tone: "accent" },
  confirmed: { label: "Confirmed", tone: "success" },
  showed: { label: "Showed", tone: "purple" },
  no_show: { label: "No-show", tone: "warning" },
  cancelled: { label: "Cancelled", tone: "danger" },
  rescheduled: { label: "Rescheduled", tone: "outline" },
};

/** What a person sets the status to; `rescheduled` is the reschedule's. */
export const SETTABLE_STATUSES: readonly AppointmentStatus[] = [
  "scheduled", "confirmed", "showed", "no_show", "cancelled",
];

/** The statuses an appointment is still on the calendar in. */
export const ACTIVE_STATUSES: readonly AppointmentStatus[] = ["scheduled", "confirmed", "showed", "no_show"];

/** Every status, in the order Manage view lists them. */
export const ALL_STATUSES = Object.keys(STATUS_INFO) as AppointmentStatus[];

export type ListTab = "upcoming" | "cancelled" | "all";

/**
 * The statuses the list asks for: its tab's, narrowed by the ones picked in Manage view.
 * `undefined` asks for every status; an empty list means nothing can match (the tab and the
 * filter disagree, e.g. Cancelled with only "Showed" picked).
 */
export function listStatuses(tab: ListTab, picked: readonly string[]): string[] | undefined {
  const base: readonly string[] | undefined =
    tab === "upcoming" ? ACTIVE_STATUSES : tab === "cancelled" ? ["cancelled"] : undefined;
  if (picked.length === 0) return base ? [...base] : undefined;
  return base ? base.filter((s) => picked.includes(s)) : [...picked];
}

const DAY = 86_400_000;

/** Manage view's days (ACL-598), as the date inputs give them (`YYYY-MM-DD`); null is open. */
export interface DateRange {
  from: string | null;
  to: string | null;
}

/** Midnight starting `day`, `plus` days on, on the viewer's clock. */
function midnight(day: string, plus = 0): number {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y!, m! - 1, d! + plus).getTime();
}

/**
 * What the list asks core-api for. Upcoming is what still stands from now; cancelled and all look
 * a year back and a year on. Manage view's days (ACL-598) replace those bounds, both days in, read
 * on the viewer's clock; upcoming still starts no earlier than now. Its statuses narrow the tab's
 * (`listStatuses`). `nothing` is a query that cannot match; `past` says it is because the days are
 * all behind upcoming's now.
 */
export function listQuery(tab: ListTab, now: number, picked: readonly string[], range: DateRange) {
  const statuses = listStatuses(tab, picked);
  const from = range.from ? midnight(range.from) : tab === "upcoming" ? now : now - 365 * DAY;
  const start = tab === "upcoming" ? Math.max(from, now) : from;
  const end = range.to ? midnight(range.to, 1) : Math.max(start, now) + 365 * DAY;
  const past = tab === "upcoming" && end <= now;
  return {
    start: new Date(start).toISOString(),
    end: new Date(end).toISOString(),
    statuses: statuses?.join(","),
    nothing: statuses?.length === 0 || end <= start,
    past,
  };
}

export function statusInfo(status: string): { label: string; tone: Tone } {
  return STATUS_INFO[status as AppointmentStatus] ?? { label: status, tone: "neutral" };
}

/** Cancelled and rescheduled appointments change no more. */
export function isClosed(status: string): boolean {
  return status === "cancelled" || status === "rescheduled";
}

/**
 * Whether the contact's reminder texts can still be turned on or off (ACL-504): until the
 * appointment starts, and never on a closed one or one that has happened, as core-api decides.
 */
export function remindersChangeable(a: Pick<Appointment, "status" | "starts_at">, now = Date.now()): boolean {
  return !isClosed(a.status) && a.status !== "showed" && a.status !== "no_show" && Date.parse(a.starts_at) > now;
}

/** Whether a booking's confirmation text was handed to the provider (ACL-504). */
export function confirmationLeft(c: AppointmentConfirmation): boolean {
  return c.status === "queued" || c.status === "sent" || c.status === "delivered";
}

/** Whether a booking's confirmation text waits for the contact's 8 am (ACL-630): kept, and not
 *  gone yet. */
export function confirmationScheduled(c: AppointmentConfirmation): boolean {
  return c.status === "scheduled";
}

/** How a booking's confirmation text stands, as the details drawer says it (ACL-504). */
export function confirmationText(c: AppointmentConfirmation): string {
  switch (c.status) {
    case "scheduled":
      return `${scheduledFor(c.send_at)}, the contact's 8 am`;
    case "queued":
      return "Sending";
    case "sent":
      return "Sent";
    case "delivered":
      return "Delivered";
    case "failed":
      return c.problem ? `Not delivered: ${c.problem}` : "Not delivered";
    case "not_sent":
      return c.problem ? `Not sent: ${c.problem}` : "Not sent";
  }
}

export const SOURCE_LABELS: Record<string, string> = {
  manual: "Booked in the console",
  conversation: "Conversation",
  api: "API",
};

/** A booking request and the key it is sent with (`idempotency_key`). */
export interface BookingAttempt {
  request: string;
  key: string;
}

/**
 * The key for a booking about to be sent. core-api answers a key it has seen with the appointment
 * that key made (ADR-085), so the same request sent again, after an answer that never came, gets
 * that appointment back instead of a second one. The key stays while the request is the same; any
 * change to it (another time, another calendar) is another booking, with a new key from `mint`.
 */
export function bookingAttempt(previous: BookingAttempt | null, request: unknown, mint: () => string): BookingAttempt {
  const print = JSON.stringify(request);
  return previous?.request === print ? previous : { request: print, key: mint() };
}

/** "Mon, Oct 5, 2026, 9:00 AM – 9:30 AM (CDT)", on the calendar's clock. */
export function timeRange(
  startIso: string,
  endIso: string,
  timezone: string,
  format: "12h" | "24h" = "12h",
  locale = "en-US",
): string {
  const day = new Intl.DateTimeFormat(locale, {
    timeZone: timezone, weekday: "short", month: "short", day: "numeric", year: "numeric",
  }).format(new Date(startIso));
  const time = (iso: string) =>
    new Intl.DateTimeFormat(locale, {
      timeZone: timezone, hour: format === "12h" ? "numeric" : "2-digit", minute: "2-digit", hour12: format === "12h",
    }).format(new Date(iso));
  const zone = new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: "short" })
    .formatToParts(new Date(startIso))
    .find((p) => p.type === "timeZoneName")?.value;
  return `${day}, ${time(startIso)} – ${time(endIso)}${zone ? ` (${zone})` : ""}`;
}

/** Where the meeting happens, as a line: the details, or for a phone meeting the number to call. */
export function locationText(kind: MeetingLocationKind, detail: string | null, contactPhone?: string | null): string {
  if (detail) return detail;
  if (kind === "phone") return contactPhone ? `Call ${contactPhone}` : "Phone call to the contact";
  return LOCATION_LABELS[kind];
}

/** One history row in words: "Booked", "Edited title, notes", "Confirmed → Showed: arrived late". */
export function eventText(e: AppointmentEvent): string {
  switch (e.kind) {
    case "created":
      return e.detail ? `Booked, ${e.detail}` : "Booked";
    case "edited":
      return e.detail ? `Edited ${e.detail}` : "Edited";
    case "status": {
      const to = statusInfo(e.to_status ?? "").label;
      const line = e.from_status ? `${statusInfo(e.from_status).label} → ${to}` : to;
      return e.detail ? `${line}: ${e.detail}` : line;
    }
    case "rescheduled":
      return e.detail ? `Rescheduled ${e.detail}` : "Rescheduled";
    case "attendee_added":
      return e.detail ? `${e.detail} joined the class` : "A contact joined the class";
    case "attendee_removed":
      return e.detail ? `${e.detail} left the class` : "A contact left the class";
    // ACL-504: on a class session `detail` names the contact; on any other, it is why not.
    case "confirmation":
      if (e.to_status === "scheduled") {
        return e.detail ? `Confirmation text to ${e.detail} scheduled for their 8 am` : "Confirmation text scheduled for the contact's 8 am";
      }
      if (e.to_status !== "not_sent" && e.to_status !== "failed") {
        return e.detail ? `Confirmation text sent to ${e.detail}` : "Confirmation text sent";
      }
      return e.detail ? `Confirmation text not sent: ${e.detail}` : "Confirmation text not sent";
    default:
      return e.detail ? `${e.kind}: ${e.detail}` : e.kind;
  }
}

/**
 * Who hosts an appointment (ACL-655, ADR-215): a rep on the calendar hosts what they book, and for
 * anyone else core-api chooses who is free (null).
 */
export function defaultHost(
  user: { id: number; role: string } | null | undefined,
  staff: readonly { user_id: number }[],
): number | null {
  return user?.role === "rep" && staff.some((s) => s.user_id === user.id) ? user.id : null;
}
